import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, AfterViewInit, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { Dealer } from '../../../interfaces/waybill.interfaces';
import * as L from 'leaflet';

@Component({
  selector: 'app-dealer-detail-popup',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './dealer-detail-popup.component.html',
  styleUrl: './dealer-detail-popup.component.css'
})
export class DealerDetailPopupComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() visible: boolean = false;
  @Input() dealer: Dealer | null = null;
  @Input() activeTab: string = 'details';
  @Output() closed = new EventEmitter<void>();
  @Output() tabChanged = new EventEmitter<string>();

  @ViewChild('mapContainer', { static: false }) mapContainer!: ElementRef;
  private map: L.Map | null = null;

  constructor(private router: Router) {}

  /** Apre la scheda anagrafica del dealer in una nuova scheda del browser */
  openDealerAnagraphic(): void {
    if (!this.dealer?.dealerCode) return;
    const url = this.router.serializeUrl(
      this.router.createUrlTree(['/anagraphic/dealers', this.dealer.dealerCode])
    );
    window.open(url, '_blank');
  }

  ngOnInit(): void {
    // Add event listener for escape key
    document.addEventListener('keydown', this.handleKeyDown.bind(this));
  }

  ngAfterViewInit(): void {
    // Initialize map when the map tab becomes active
    if (this.activeTab === 'map' && this.dealer?.dealerLatitude && this.dealer?.dealerLongitude) {
      setTimeout(() => this.initializeMap(), 100);
    }
  }

  ngOnDestroy(): void {
    // Remove event listener
    document.removeEventListener('keydown', this.handleKeyDown.bind(this));
    // Destroy map if it exists
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  }

  private handleKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape' && this.visible) {
      this.close();
    }
  }

  close(): void {
    this.closed.emit();
  }

  setActiveTab(tab: string): void {
    this.activeTab = tab;
    this.tabChanged.emit(tab);
    
    // Initialize map when switching to map tab
    if (tab === 'map' && this.dealer?.dealerLatitude && this.dealer?.dealerLongitude) {
      setTimeout(() => this.initializeMap(), 100);
    }
  }

  private initializeMap(): void {
    if (!this.mapContainer || !this.dealer?.dealerLatitude || !this.dealer?.dealerLongitude) {
      return;
    }

    // Destroy existing map if it exists
    if (this.map) {
      this.map.remove();
      this.map = null;
    }

    const lat = this.dealer.dealerLatitude;
    const lng = this.dealer.dealerLongitude;

    // Initialize the map
    this.map = L.map(this.mapContainer.nativeElement, {
      center: [lat, lng],
      zoom: 15,
      zoomControl: true,
      attributionControl: true
    });

    // Add tile layer (OpenStreetMap)
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 19
    }).addTo(this.map);

    // Custom marker icon
    const customIcon = L.icon({
      iconUrl: 'assets/img/marker-custom.png',
      iconSize: [32, 32],
      iconAnchor: [16, 32],
      popupAnchor: [0, -32]
    });

    // Add marker with popup
    const marker = L.marker([lat, lng], { icon: customIcon }).addTo(this.map);
    
    const popupContent = `
      <div class="map-popup">
        <strong>${this.dealer.dealerName || this.dealer.dealerLegalName || 'Dealer'}</strong><br>
        ${this.dealer.dealerOperatingAddressStreet || ''} ${this.dealer.dealerOperatingAddressNumber || ''}<br>
        ${this.dealer.dealerOperatingAddressCity || ''} ${this.dealer.dealerOperatingAddressProvince || ''}<br>
        <small>Lat: ${lat}, Lng: ${lng}</small>
      </div>
    `;
    
    marker.bindPopup(popupContent).openPopup();

    // Invalidate size to fix display issues
    setTimeout(() => {
      if (this.map) {
        this.map.invalidateSize();
      }
    }, 100);
  }

  copyToClipboard(text: string): void {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).then(() => {
        console.log('Text copied to clipboard:', text);
      }).catch(err => {
        console.error('Failed to copy text: ', err);
      });
    } else {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "fixed";
      textArea.style.opacity = "0";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        console.log('Text copied to clipboard:', text);
      } catch (err) {
        console.error('Fallback: Unable to copy', err);
      }
      document.body.removeChild(textArea);
    }
  }

  getCoordinatesMapUrl(): string {
    if (this.dealer?.dealerLatitude && this.dealer?.dealerLongitude) {
      return `https://www.google.com/maps?q=${this.dealer.dealerLatitude},${this.dealer.dealerLongitude}&z=15`;
    }
    return '';
  }

  hasValidCoordinates(): boolean {
    return !!(this.dealer?.dealerLatitude && this.dealer?.dealerLongitude);
  }

  getStatusBadgeClass(): string {
    switch (this.dealer?.dealerStatus) {
      case 'ACTIVE':
        return 'status-executed';
      case 'PENDING':
        return 'status-pending';
      case 'SUSPENDED':
      case 'TERMINATED':
        return 'status-failed';
      default:
        return 'status-unknown';
    }
  }
}
